/// <amd-module name="Test/Analytics/TestInsightsHistoryList/ControllerViews/TestInsightsTestHistoryList" />
import { CheckboxVisibility, ConstrainMode, DetailsListLayoutMode, IColumn, SelectionMode } from "OfficeFabric/DetailsList";
import * as React from "react";
import { TestOutcomeStringMap } from "Test/Analytics/Common/Constants";
import { INextDataPageToken, ITestContext, ITestReportContext, TreeNodeType } from "Test/Analytics/Common/Types";
import * as TcmTelemetryConstants from "Test/Analytics/Common/Utils/TcmTelemetryConstants";
import { publishEvent } from "Test/Analytics/Common/Utils/TelemetryService";
import { TestInsightsTestHistoryListActionsCreator } from "Test/Analytics/TestInsightsHistoryList/Actions/TestInsightsTestHistoryListActionsCreator";
import * as Resources from "Test/Analytics/TestInsightsHistoryList/Resources";
import {
    ITestInsightsTestHistoryListState,
    TestInsightsTestHistoryListStore
} from "Test/Analytics/TestInsightsHistoryList/Stores/TestInsightsTestHistoryListStore";
import { IconDetails, ITestInsightsTestHistoryListItem, TestInsightsTestHistoryColumnIndices } from "Test/Analytics/TestInsightsHistoryList/Types";
import { ClickableLabel } from "Test/Analytics/TestResultsGridCommon/Components/ClickableLabel";
import { TestResultsContextType } from "Test/common-util/Contracts/Test";
import * as Common from "Test/common-util/Contracts/ViewContracts";
import { css, IVssComponentContext, IVssComponentProperties, VssComponent } from "VSS/Platform/Layout";
import { Icon } from "VSSUI/Icon";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";
import { VssDetailsList } from "VSSUI/VssDetailsList";

export interface ITestHistoryListProps extends IVssComponentProperties {
    testReportContext: ITestReportContext;
    store: TestInsightsTestHistoryListStore;
    testContext: ITestContext;
    actionCreator: TestInsightsTestHistoryListActionsCreator;
    onGridRowClick: (row: Common.ITestResultTreeData) => void;
    onMinWidthChange: (minWidth: number) => void;
    handleActiveRow?: (activeTestItem?: HTMLElement) => void;
}

interface IExtendedTestInsightsTestHistoryListState extends ITestInsightsTestHistoryListState {
    minWidth: number;
}

export class TestInsightsTestHistoryList extends VssComponent<ITestHistoryListProps, IExtendedTestInsightsTestHistoryListState> {
    private minWidth: number;

    public constructor(props: ITestHistoryListProps, context: IVssComponentContext) {
        super(props, context);
        this.state = {} as IExtendedTestInsightsTestHistoryListState;
    }

    public componentDidMount(): void {
        if (this.props) {
            this.props.store.addChangedListener(this._onStoreUpdate);
            this.setState(this.props.store.getState());
        }
    }

    public componentWillUnmount(): void {
        this.props.store.removeChangedListener(this._onStoreUpdate);
    }

    public componentDidUpdate(): void {
        if (this.props && this.props.onMinWidthChange && this.state.minWidth !== this.minWidth) {
            this.props.onMinWidthChange(this.minWidth);
        }
    }

    public render(): JSX.Element {
        return (
            <div
                className="testinsights-analytics-report-view-testhistory-container flex-grow curved-border-white-background"
                style={this.props.style}
            >
                {this.state.items ? (
                    <VssDetailsList
                        items={this.state.items}
                        usePresentationStyles={true}
                        selectionPreservedOnEmptyClick={false}
                        selectionMode={SelectionMode.single}
                        layoutMode={DetailsListLayoutMode.fixedColumns}
                        checkboxVisibility={CheckboxVisibility.hidden}
                        columns={this._getColumns()}
                        constrainMode={ConstrainMode.unconstrained}
                        compact={true}
                        onItemInvoked={this._onActiveItemChanged}
                    />
                ) : (
                    <Spinner className="analytics-card-spinner flex-grow flex-column" size={SpinnerSize.large} />
                )}
            </div>
        );
    }

    private _onActiveItemChanged = (item: ITestInsightsTestHistoryListItem, index: number, ev: Event): void => {
        if (item.nodeType !== TreeNodeType.showMore) {
            const treeData: Common.ITestResultTreeData = {
                runId: item.itemkey.testRunId,
                resultId: item.itemkey.testResultId,
                nodeType: Common.TreeNodeType.leaf,
                isTestCaseRow: true,
                duration: item.duration,
                groupId: ""
            } as Common.ITestResultTreeData;

            this.props.onGridRowClick(treeData);

            if (this.props.handleActiveRow) {
                this.props.handleActiveRow(ev.target as HTMLElement);
            }
            const telemetryData: { [property: string]: string | number } = {
                [TcmTelemetryConstants.Outcome]: item.outcome
            };
            publishEvent(
                this.context.pageContext,
                TcmTelemetryConstants.featureTestAX_TestDetailsView,
                telemetryData,
                this.props.testReportContext,
                this.props.store.getConfValues()
            );
        }
    };

    private _getColumns(): IColumn[] {
        const columns: IColumn[] = [
            {
                fieldName: Resources.OutcomeText,
                key: TestInsightsTestHistoryColumnIndices.Outcome.toString(),
                name: Resources.OutcomeText,
                ariaLabel: Resources.OutcomeText,
                minWidth: 200,
                isResizable: true,
                onRender: this._onRenderOutcomeColumnItem
            },
            {
                fieldName: Resources.DateText,
                key: TestInsightsTestHistoryColumnIndices.Date.toString(),
                name: Resources.DateText,
                ariaLabel: Resources.DateText,
                minWidth: 200,
                isResizable: true,
                onRender: (item: ITestInsightsTestHistoryListItem, index?: number, column?: IColumn) => {
                    return item.nodeType !== TreeNodeType.showMore ? <span>{item.date}</span> : null;
                }
            },
            {
                fieldName: Resources.DurationText,
                key: TestInsightsTestHistoryColumnIndices.Duration.toString(),
                name: Resources.DurationText,
                ariaLabel: Resources.DurationText,
                minWidth: 200,
                isResizable: true,
                onRender: (item: ITestInsightsTestHistoryListItem, index?: number, column?: IColumn) => {
                    return item.nodeType !== TreeNodeType.showMore ? <span aria-label={item.durationAriaLabel}>{item.duration}</span> : null;
                }
            },
            {
                fieldName: Resources.BranchText,
                key: TestInsightsTestHistoryColumnIndices.Branch.toString(),
                name: Resources.BranchText,
                ariaLabel: Resources.BranchText,
                minWidth: 300,
                isResizable: true,
                onRender: (item: ITestInsightsTestHistoryListItem, index?: number, column?: IColumn) => {
                    return item.nodeType !== TreeNodeType.showMore ? <span>{item.branch}</span> : null;
                }
            }
        ];

        if (this.props.testReportContext && this.props.testReportContext.contextType === TestResultsContextType.Release) {
            columns.push({
                fieldName: Resources.Stage,
                key: TestInsightsTestHistoryColumnIndices.Environment.toString(),
                name: Resources.Stage,
                ariaLabel: Resources.Stage,
                minWidth: 300,
                isResizable: true,
                onRender: (item: ITestInsightsTestHistoryListItem, index?: number, column?: IColumn) => {
                    return item.nodeType !== TreeNodeType.showMore ? <span>{item.environmentRef.name}</span> : null;
                }
            });
        }

        this.minWidth = 100;
        columns.forEach(column => {
            this.minWidth += column.minWidth;
        });
        return columns;
    }

    private _onRenderOutcomeColumnItem = (item: ITestInsightsTestHistoryListItem, index: number, column?: IColumn): JSX.Element => {
        if (item.nodeType === TreeNodeType.showMore) {
            return (
                <ClickableLabel
                    value={item.itemName}
                    onClick={() => {
                        this._onShowMoreTestHistoryList(this.props.store.getNextPageToken());
                    }}
                    showLoadingOnClick={true}
                />
            );
        } else {
            return (
                <div className={css("flex", "flex-center", "test-results-cell")}>
                    {this._getOutcomeIconElement(item)}
                    <span>{item.outcome}</span>
                </div>
            );
        }
    };

    private _onShowMoreTestHistoryList(nextPageToken?: INextDataPageToken) {
        this.props.actionCreator.showMoreTestHistoryItems(this.props.testReportContext, this.props.store.getConfValues(), nextPageToken);
    }

    private _getOutcomeIconElement(resultData: ITestInsightsTestHistoryListItem): JSX.Element {
        const iconDetails: IconDetails = this._getIconDetails(resultData.outcome);
        return (
            <div className="testresult-outcome-icon">
                <Icon className={iconDetails.className} iconName={iconDetails.iconName} />
            </div>
        );
    }

    private _getIconDetails(outcome: string): IconDetails {
        switch (outcome) {
            case TestOutcomeStringMap.Unspecified:
                return { iconName: "Help", className: "icon-inconclusive" };
            case TestOutcomeStringMap.None:
                return { iconName: "Remove", className: "icon-inconclusive" };
            case TestOutcomeStringMap.Passed:
                return { iconName: "CheckMark", className: "icon-passed" };
            case TestOutcomeStringMap.Failed:
                return { iconName: "Cancel", className: "icon-failed" };
            case TestOutcomeStringMap.Inconclusive:
                return { iconName: "Unknown", className: "icon-inconclusive" };
            case TestOutcomeStringMap.Timeout:
                return { iconName: "Clock", className: "icon-inconclusive" };
            case TestOutcomeStringMap.Aborted:
                return { iconName: "CircleStop", className: "icon-failed" };
            case TestOutcomeStringMap.Blocked:
                return { iconName: "Blocked2", className: "icon-failed" };
            case TestOutcomeStringMap.NotExecuted:
                return { iconName: "NotExecuted", className: "icon-inconclusive" };
            case TestOutcomeStringMap.Warning:
                return { iconName: "Warning", className: "icon-warning" };
            case TestOutcomeStringMap.Error:
                return { iconName: "Error", className: "icon-failed" };
            case TestOutcomeStringMap.NotApplicable:
                return { iconName: "Blocked", className: "icon-inconclusive" };
            case TestOutcomeStringMap.Paused:
                return { iconName: "CirclePause", className: "icon-inconclusive" };
            case TestOutcomeStringMap.InProgress:
                return { iconName: "MSNVideos", className: "icon-inprogress" };
            case TestOutcomeStringMap.NotImpacted:
                return { iconName: "NotImpactedSolid", className: "icon-inconclusive" };
            default:
                return { iconName: "Remove", className: "icon-inconclusive" };
        }
    }

    private _onStoreUpdate = () => {
        this.setState(this.props.store.getState());
    };
}
